import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { ThemeProvider } from '@/components/theme-provider';
import { Toaster } from '@/components/atoms/sonner';
import { TooltipProvider } from '@/components/atoms/tooltip';
import { usePrefersDarkScheme } from '@/hooks/usePrefersDarkScheme';

/**
 * Public pages drawn in both palettes. They follow the payer's device setting
 * instead of the forced light theme the rest of the (public) group gets.
 */
const DEVICE_THEME_ROUTE_IDS: ReadonlySet<string> = new Set([
  '/(public)/payment-method-request/$token',
  '/(public)/repair-payment-method/$token',
  '/(public)/repair-membership/$token',
]);

export const Route = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  // Public pages (estimate/invoice/booking links, etc.) hardcode light surfaces.
  // A dark theme persisted from the dashboard puts `.dark` on <html>, so token-driven
  // atoms (Input, Checkbox, ...) render near-white text on those white cards (NOT-652).
  // Force the light palette for the whole (public) route group; dashboard routes keep
  // the user's chosen theme, and next-themes restores it as soon as we leave the group.
  const publicTheme = useRouterState({
    select: (state) =>
      state.matches.some((match) => DEVICE_THEME_ROUTE_IDS.has(match.routeId))
        ? 'device'
        : state.matches.some((match) => match.routeId.startsWith('/(public)/'))
          ? 'light'
          : null,
  });
  const prefersDark = usePrefersDarkScheme();
  const forcedTheme =
    publicTheme === 'device' ? (prefersDark ? 'dark' : 'light') : (publicTheme ?? undefined);

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem
      disableTransitionOnChange
      forcedTheme={forcedTheme}
    >
      <TooltipProvider>
        <Outlet />
        <Toaster />
      </TooltipProvider>
    </ThemeProvider>
  );
}
